'use client'

import * as React from 'react'
import { HoverCard as HoverCardPrimitive } from 'radix-ui'
import { tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'

export type HoverCardProps = React.ComponentProps<
  typeof HoverCardPrimitive.Root
>
const HoverCard = HoverCardPrimitive.Root

const HoverCardTrigger = HoverCardPrimitive.Trigger

export const hoverCardContentVariants = tv({
  defaultVariants: {
    textSize: 'sm',
  },

  slots: {
    content:
      'bg-tremor-background text-tremor-content-strong data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-40 min-w-64 origin-(--radix-hover-card-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden duration-100',
    text: null,
  },

  variants: {
    padding: {
      compact: { content: 'p-3' },
      none: { content: 'p-0' },
    },
    size: {
      auto: { content: 'w-auto min-w-0' },
      lg: { content: 'max-w-lg' },
      md: { content: 'w-96' },
      sm: { content: 'max-w-72' },
    },
    textSize: {
      sm: { text: 'text-sm' },
      xs: { content: 'text-xs', text: 'text-xs' },
    },
  },
})

export type HoverCardContentProps = Omit<
  React.ComponentProps<typeof HoverCardPrimitive.Content>,
  'className'
> &
  VariantProps<typeof hoverCardContentVariants>

function HoverCardContent({
  align = 'center',
  children,
  padding,
  sideOffset = 4,
  size,
  textSize,
  ...props
}: HoverCardContentProps) {
  const classNames = hoverCardContentVariants({ padding, size, textSize })

  return (
    <HoverCardPrimitive.Portal>
      <HoverCardPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        className={classNames.content()}
        {...props}
      >
        {typeof children === 'string' ? (
          <p className={classNames.text()}>{children}</p>
        ) : (
          children
        )}
      </HoverCardPrimitive.Content>
    </HoverCardPrimitive.Portal>
  )
}

export { HoverCard, HoverCardTrigger, HoverCardContent }
